import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslate } from "../context/TranslationContext";
import {
  useCreateGuidelineMutation,
  useGetGuidelineQuery,
  useUpdateGuidelineMutation,
} from "../redux/Apis/Guideline";
import usePresignedUpload from "./usePresignedUpload";

const usePostGuidelineController = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("community");
  const { user } = useSelector((state) => state.authSlice);
  const [content, setContent] = useState("");
  const [imgorgif, setImgorgif] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const toastShownRef = useRef(false);
  const { data: guidelineData, isSuccess: isFetchSuccess } =
    useGetGuidelineQuery({ id, params: { lang } }, { skip: !id });
  const [createGuideline, { isSuccess: isPostSuccess, isLoading: isPosting }] =
    useCreateGuidelineMutation();

  const [
    updateGuideline,
    { isSuccess: isUpdateSuccess, isLoading: isUpdating },
  ] = useUpdateGuidelineMutation();

  const { uploadFileWithPresignedUrl } = usePresignedUpload();

  useEffect(() => {
    if (id && isFetchSuccess && guidelineData?.data?.content) {
      setContent(guidelineData?.data?.content);
      setImgorgif(guidelineData?.data?.imgorgif || "");
    }
  }, [id, isFetchSuccess, guidelineData]);

  useEffect(() => {
    if ((isPostSuccess || isUpdateSuccess) && !toastShownRef.current) {
      toastShownRef.current = true;

      toast.success(id ? t("toastPostUpdated") : t("toastPostCreated"));

      setContent("");
      setImgorgif("");
      setThumbnailFile(null);
      setPreviewUrl(null);
      setIsRefetching(true);

      if (id) {
        navigate("/community");
      }
    }
  }, [isPostSuccess, isUpdateSuccess, navigate, id, t]);

  const onChangeContent = useCallback((e) => {
    setContent(e?.target?.value);
  }, []);

  const onChangeImgorgif = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, []);

  const onRemoveSelectedFile = useCallback(() => {
    setThumbnailFile(null);
    setPreviewUrl(null);

    const fileInputs = document.querySelectorAll("input[type='file']");
    fileInputs.forEach((input) => {
      input.value = "";
    });
  }, []);

  const onClickPost = useCallback(async () => {
    if (!user) {
      toast.info(t("loginToPost"));
      return;
    }

    if (!content.trim()) {
      toast.error(t("toastEnterContent"));
      return;
    }

    if(!thumbnailFile && !imgorgif){
      toast.error(t("toastSelectMedia"));
      return;
    }

    let mediaKey = imgorgif;

    if (thumbnailFile instanceof File) {
      const key = await uploadFileWithPresignedUrl(thumbnailFile);
      if (!key) return;
      mediaKey = key;
    }

    try {
      const payload = { content };
      if (mediaKey) payload.imgorgif = mediaKey;

      if (id) {
        await updateGuideline({ id, body: payload, params: { lang } }).unwrap();
      } else {
        await createGuideline({ payload, params: { lang } }).unwrap();
      }
    } catch (error) {
      console.error("Post/Update failed:", error);
      toast.error(error?.data?.message || t("toastErrorGeneric"));
    }
  }, [
    user,
    content,
    thumbnailFile,
    imgorgif,
    id,
    createGuideline,
    updateGuideline,
    uploadFileWithPresignedUrl,
    t,
  ]);

  return {
    values: {
      isLoading: isPosting || isUpdating,
      isRefetching,
      content,
      imgorgif,
      previewUrl,
      isEditing: !!id,
    },
    functions: {
      onClickPost,
      onChangeContent,
      onChangeImgorgif,
      onRemoveSelectedFile,
    },
  };
};

export default usePostGuidelineController;
